import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { useAuth } from "../lib/auth";
import AppelPanel from "../components/AppelPanel";
import { Toaster } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";
import { downloadBlob } from "../lib/download";

function AppelHeader({ classes, classId, setClassId, onDownload }) {
  return (
    <div className="mt-6 flex flex-wrap items-end justify-between gap-6 pb-8 border-b border-[var(--parchment-2)]">
      <div>
        <div className="eyebrow">Appel · 08h10 · 13h00 · 14h00 · 16h45 · Étude du soir (16h45–18h00)</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Appel de classe</h1>
        <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
          Tous les élèves sont présents par défaut. Sélectionnez la classe et le créneau,
          cochez les absents et retardataires, puis <strong>validez l'appel</strong> pour informer la direction.
          Un élève noté absent peut être corrigé en « Retard » s'il arrive ensuite.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="eyebrow">Classe</span>
          <select
            value={classId}
            onChange={(e) => setClassId(e.target.value)}
            className="px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm"
            data-testid="appel-class-select"
          >
            {classes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <button
          onClick={onDownload}
          disabled={!classId}
          className="btn-secondary !py-2 !px-4 text-sm flex items-center gap-2"
          title="Feuille d'appel papier à imprimer (5 créneaux, date du jour) — en cas de panne internet"
          data-testid="appel-sheet-btn"
        >
          <Printer size={15} /> Feuille d'appel (PDF)
        </button>
      </div>
    </div>
  );
}

export default function Appel() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");

  useEffect(() => {
    api.get("/classes").then(({ data }) => {
      const real = data.filter((c) => !c.id.startsWith("lvl:"));
      setClasses(real);
      if (real.length) setClassId(real[0].id);
    });
  }, []);

  const canEdit = ["teacher", "assoc_admin"].includes(user?.role);

  const downloadSheet = () => {
    const klass = classes.find((c) => c.id === classId);
    if (!klass) return;
    const today = new Date().toISOString().slice(0, 10);
    downloadBlob(
      `/classes/${classId}/appel-sheet.pdf`,
      `appel-${klass.name.replace(/ /g, "-")}-${today}.pdf`,
      `Feuille d'appel ${klass.name} téléchargée`,
      "Échec du téléchargement de la feuille d'appel",
      { date: today }
    );
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/teacher" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="appel-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <AppelHeader classes={classes} classId={classId} setClassId={setClassId} onDownload={downloadSheet} />

        <div className="mt-8">
          {classId ? <AppelPanel classId={classId} canEdit={canEdit} /> : <div className="text-[var(--ink-soft)]">Aucune classe.</div>}
        </div>
      </main>
    </div>
  );
}
